import { useEffect, useState } from "react";
import { apiPcf, type LoteBusca } from "../lib/api";
import { MsgModal } from "./MsgModal";
import { useOrdenacao, ThOrdena } from "../hooks/useOrdenacao";

// CALLLOT — pesquisa de lotes com saldo (KG por peso; PC/PÇ por peças), filtros de
// largura/espessura/diâmetro/comprimento. Usada no roteiro da PCF e no acerto de estoque.

const nf2 = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nf3 = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const fmt2 = (n: number) => nf2.format(n || 0);
// Filtros com máscara ZZZ.ZZ9,999 no form calllot (Pesquisas.ppj) -> até 3 casas.
const limparDec = (v: string) => {
  // ponto do teclado numérico vira vírgula (com vírgula presente, ponto é milhar e sai)
  let s = (v.includes(",") ? v.replace(/\./g, "") : v.replace(/\./g, ",")).replace(/[^0-9,]/g, "");
  const i = s.indexOf(",");
  if (i >= 0) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/,/g, "").slice(0, 3);
  return s;
};

interface Props {
  onEscolher: (numlot: number) => void;
  onFechar: () => void;
}

export function PesquisaLotesModal({ onEscolher, onFechar }: Props) {
  const [filtro, setFiltro] = useState({ largura: "", espessura: "", diametro: "", comprimento: "" });
  const [lotes, setLotes] = useState<LoteBusca[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const ord = useOrdenacao(lotes, {
    lot: (x) => x.numlot, loc: (x) => x.nomloc, pro: (x) => x.nompro, un: (x) => x.unidade, larg: (x) => x.largura,
    esp: (x) => x.espessura, comp: (x) => x.comprimento, diam: (x) => x.diametro, peso: (x) => x.saldoPeso, pec: (x) => x.saldoPecas,
  });

  async function pesquisar(f = filtro) {
    try {
      setErro(null);
      setLotes(await apiPcf.lotes(f));
    } catch (ex) {
      setErro((ex as Error).message);
    }
  }

  useEffect(() => {
    void pesquisar();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onFechar();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="pcf-modal-bg" onClick={onFechar}>
      <div className="pcf-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pcf-modal-topo">
          <b>Pesquisa de Lotes</b>
          <button className="btn-secundario" onClick={onFechar}>Fechar</button>
        </div>
        <div className="pcf-grid">
          {(["largura", "espessura", "diametro", "comprimento"] as const).map((k) => (
            <label key={k} className="pcf-campo pcf-sm">
              <span>{{ largura: "Largura", espessura: "Espessura", diametro: "Diâmetro Externo", comprimento: "Comprimento" }[k]}</span>
              <input
                className="num"
                inputMode="decimal"
                value={filtro[k]}
                onChange={(e) => setFiltro({ ...filtro, [k]: limparDec(e.target.value) })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void pesquisar();
                  }
                }}
              />
            </label>
          ))}
          <button className="btn-acao" onClick={() => void pesquisar()}>
            Pesquisar
          </button>
        </div>
        <p className="pcf-hint">Para selecionar, clique no registro desejado.</p>
        <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />
        <div className="tabela-wrap">
          <table className="tabela-pcfs">
            <thead>
              <tr>
                {/* calllot LEITURA (TELA-1.prc L344-353) — colunas do form: */}
                <ThOrdena ord={ord} col="lot" className="num">Lote</ThOrdena>
                <ThOrdena ord={ord} col="loc">Local de Estoque</ThOrdena>
                <ThOrdena ord={ord} col="pro">Produto</ThOrdena>
                <ThOrdena ord={ord} col="un">Unidade</ThOrdena>
                <ThOrdena ord={ord} col="larg" className="num">Largura</ThOrdena>
                <ThOrdena ord={ord} col="esp" className="num">Espessura</ThOrdena>
                <ThOrdena ord={ord} col="comp" className="num">Comprimento</ThOrdena>
                <ThOrdena ord={ord} col="diam" className="num">Diâmetro Ext</ThOrdena>
                <ThOrdena ord={ord} col="peso" className="num">Peso</ThOrdena>
                <ThOrdena ord={ord} col="pec" className="num">Qtd. Peças</ThOrdena>
              </tr>
            </thead>
            <tbody>
              {lotes && lotes.length === 0 && (
                <tr>
                  <td colSpan={10} className="plan-vazio">
                    Nenhum lote com saldo para estes filtros.
                  </td>
                </tr>
              )}
              {ord.linhas.map((x) => (
                <tr key={x.numlot} className="row-click" onClick={() => onEscolher(x.numlot)}>
                  <td className="num">{x.numlot}</td>
                  <td>{x.nomloc}</td>
                  <td>{x.nompro}</td>
                  <td>{x.unidade}</td>
                  <td className="num">{fmt2(x.largura)}</td>
                  <td className="num">{fmt2(x.espessura)}</td>
                  <td className="num">{x.comprimento}</td>
                  <td className="num">{fmt2(x.diametro)}</td>
                  <td className="num">{nf3.format(x.saldoPeso)}</td>
                  <td className="num">{fmt2(x.saldoPecas)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
